import {
  normalizeStationCameraTarget,
  stationCameraRecordingIdentity,
  stationCameraTargetKey,
  stationCameraTargetLabel,
} from "./stationCameraBinding";

function translate(t, key, fallback) {
  if (typeof t !== "function") return fallback;
  const value = t(key);
  if (!value || value === key) return fallback;
  return value;
}

/** Normalize `sessionCameraPhones` rows to `{ userId, tableNumber }`. */
export function normalizeCameraPhones(phones) {
  const out = [];
  for (const phone of phones || []) {
    const userId =
      phone?.user_id != null
        ? String(phone.user_id).trim()
        : phone?.userId != null
          ? String(phone.userId).trim()
          : "";
    const table = Number(phone?.table_number ?? phone?.tableNumber);
    const tableNumber = Number.isFinite(table) && table > 0 ? table : null;
    if (!userId && tableNumber == null) continue;
    out.push({ userId, tableNumber });
  }
  return out;
}

/**
 * Camera phones a coach can start for an off-table chrono exercise —
 * only phones currently connected with their camera recorder on.
 * A phone pinned to a table is offered as that table's camera; any other
 * phone is offered under its player's name. A dual-role account (coach on
 * one phone, player on another) keeps the player-role phone: same user id,
 * different device.
 *
 * `players` is this station's list, so a player moved to another station's
 * table drops out with their phone. Once players are seated, table cameras
 * are limited to the tables they sit at.
 *
 * @param {{
 *   players?: Array<object>,
 *   cameraPhones?: Array<{ userId?: string, tableNumber?: number|null }>,
 *   coachUserId?: string|null,
 *   preferredStationCamera?: object|null,
 *   tsgId?: string|null,
 *   stationName?: string|null,
 *   t?: Function,
 * }} input
 */
export function buildChronoCameraOptions({
  players = [],
  cameraPhones = [],
  coachUserId = null,
  preferredStationCamera = null,
  tsgId = null,
  stationName = null,
  t,
} = {}) {
  const phones = normalizeCameraPhones(cameraPhones);
  const userPhones = new Set(
    phones.filter((p) => p.userId && p.tableNumber == null).map((p) => p.userId)
  );
  const seatedTables = new Set();
  for (const p of players || []) {
    const n = Number(p?.tableNumber ?? p?.table_number);
    if (Number.isFinite(n) && n > 0) seatedTables.add(n);
  }
  const tablePhones = [
    ...new Set(phones.filter((p) => p.tableNumber != null).map((p) => p.tableNumber)),
  ]
    .filter((n) => seatedTables.size === 0 || seatedTables.has(n))
    .sort((a, b) => a - b);

  const opts = [];
  const seen = new Set();
  const listedUsers = new Set();
  const push = (opt) => {
    const normalized = normalizeStationCameraTarget(opt);
    if (!normalized) return;
    const key = stationCameraTargetKey(normalized);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const identity = stationCameraRecordingIdentity(normalized);
    if (identity?.kind === "user") listedUsers.add(identity.userId);
    opts.push({
      ...normalized,
      label: opt.label || stationCameraTargetLabel(normalized, t, { stationName }),
    });
  };

  const preferred = normalizeStationCameraTarget(preferredStationCamera);
  const preferredIdentity = stationCameraRecordingIdentity(preferred);
  const preferredOnline =
    preferredIdentity?.kind === "table"
      ? tablePhones.includes(preferredIdentity.tableNumber)
      : preferredIdentity?.kind === "user"
        ? userPhones.has(preferredIdentity.userId)
        : false;
  if (preferred && preferredOnline) {
    push({
      ...preferred,
      label:
        preferred.label ||
        stationCameraTargetLabel(preferred, t, { stationName }),
    });
  }

  const coachId = coachUserId != null ? String(coachUserId) : "";
  const playerPhoneLabel = translate(
    t,
    "liveTrainingChronoPlayerPhone",
    "Player phone"
  );
  const myPlayerPhoneLabel = translate(
    t,
    "liveTrainingChronoMyPlayerPhone",
    "My player phone"
  );

  for (const p of players || []) {
    if (!p?.userId) continue;
    const userId = String(p.userId);
    if (!userPhones.has(userId) || listedUsers.has(userId)) continue;
    const isCoachPlayer = Boolean(coachId) && userId === coachId;
    const name = p.name || userId;
    const table = Number(p.tableNumber ?? p.table_number);
    const tableLabel =
      Number.isFinite(table) && table > 0
        ? (typeof t === "function"
            ? t("table_with_number", { number: table })
            : null) || `Table ${table}`
        : null;
    const base = isCoachPlayer ? `${name} · ${playerPhoneLabel}` : name;
    push({
      kind: "user",
      userId,
      playerId: p.playerId != null ? String(p.playerId) : null,
      label: tableLabel ? `${base} · ${tableLabel}` : base,
      tsgId: tsgId != null ? String(tsgId) : null,
    });
  }

  if (coachId && userPhones.has(coachId) && !listedUsers.has(coachId)) {
    push({
      kind: "user",
      userId: coachId,
      label: myPlayerPhoneLabel,
      tsgId: tsgId != null ? String(tsgId) : null,
    });
  }

  for (const n of tablePhones) {
    push({
      kind: "table",
      tableNumber: n,
      label:
        (typeof t === "function"
          ? t("liveTrainingChronoTableCamera", { number: n })
          : null) || `Table ${n} camera`,
    });
  }

  return opts;
}
